"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appFileBlobKey = appFileBlobKey;
exports.appFileBodyBytes = appFileBodyBytes;
exports.appFileBody = appFileBody;
exports.memoryAppFiles = memoryAppFiles;
exports.storedAppFile = storedAppFile;
exports.storedAppFiles = storedAppFiles;
exports.appBaseConfigFiles = appBaseConfigFiles;
exports.withStoredAppFiles = withStoredAppFiles;
const app_artifact_policy_1 = require("./app-artifact-policy");
const app_base_contract_1 = require("./app-base-contract");
const file_signature_1 = require("./file-signature");
/**
 * The key of an App file in the blob register of its workspace: the SHA-256
 * of its bytes as base64url without padding, the key every workspace blob
 * has. A file names no other key, so its body is always the bytes its
 * metadata describes. The server stores App files under it, the settings
 * measure names it, the StreamKit reads it.
 */
function appFileBlobKey(sha256Hex) {
    let binary = '';
    for (let index = 0; index < sha256Hex.length; index += 2)
        binary += String.fromCharCode(Number.parseInt(sha256Hex.slice(index, index + 2), 16));
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}
/** The bytes of an App file body, for a reader that needs them whole. */
async function appFileBodyBytes(body) {
    const chunks = [];
    for await (const chunk of body)
        chunks.push(Buffer.from(chunk));
    return Buffer.concat(chunks);
}
/** The body of an App file whose bytes are already at hand. */
async function* appFileBody(content) {
    yield content;
}
/**
 * App file bodies by blob key in memory, for a StreamKit folder or package
 * read on one machine. A body names the file it was kept for; a file it
 * does not hold is refused with its path.
 */
function memoryAppFiles() {
    const contents = new Map();
    return {
        // The server refuses a binary file whose bytes are another type; so
        // does a folder or package read here, with the path.
        store: async (file, body) => {
            const content = await appFileBodyBytes(body);
            const type = (0, app_base_contract_1.normalizeAppArtifactContentType)(file.contentType);
            if (app_artifact_policy_1.APP_BASE_BINARY_CONTENT_TYPES.has(type) &&
                (0, file_signature_1.detectImageMime)(content.subarray(0, file_signature_1.FILE_SIGNATURE_BYTES)) !== type)
                throw new Error(`App file ${file.path} is not of its type ${type}`);
            contents.set(appFileBlobKey(file.sha256), content);
        },
        bodies: async (file) => {
            const content = contents.get(appFileBlobKey(file.sha256));
            if (!content)
                throw new Error(`App file ${file.path} has no body`);
            return appFileBody(content);
        },
    };
}
/** An App file as the server stores it: its metadata and blob key, no body. */
function storedAppFile(file) {
    return {
        path: file.path,
        contentType: file.contentType,
        sha256: file.sha256,
        bytes: file.bytes,
        storageKey: appFileBlobKey(file.sha256),
    };
}
/** The files of an App or artifact as the server stores them. */
function storedAppFiles(files) {
    return Object.fromEntries(Object.entries(files).map(([path, file]) => [path, storedAppFile(file)]));
}
/** Every App file of the settings: those of each App and of the artifact. */
function appBaseConfigFiles(config) {
    return [
        ...Object.values(config.appDefinitions).flatMap(definition => Object.values(definition.files)),
        ...Object.values(config.appArtifact?.files ?? {}),
    ];
}
/**
 * The settings as the Config tables hold them: every App file by its blob,
 * as the server leaves them once it has stored the bodies.
 */
function withStoredAppFiles(config) {
    return {
        ...config,
        appDefinitions: Object.fromEntries(Object.entries(config.appDefinitions).map(([id, definition]) => [
            id,
            { ...definition, files: storedAppFiles(definition.files) },
        ])),
        ...(config.appArtifact
            ? {
                appArtifact: {
                    ...config.appArtifact,
                    files: storedAppFiles(config.appArtifact.files),
                },
            }
            : {}),
    };
}
